import { ArrowDownRight, Leaf } from "lucide-react";
import { motion } from "framer-motion";
import { ATHENA } from "@/constants/testIds";

const HERO_IMG =
  "https://images.unsplash.com/photo-1771015138120-319470ad8f04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMG1vbGVjdWxhciUyMHN0cnVjdHVyZXxlbnwwfHx8fDE3ODE2Mjc0NTF8MA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid={ATHENA.heroSection}
      className="relative pt-32 md:pt-36 pb-8 md:pb-10 overflow-hidden"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        {/* Left: textual */}
        <div className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="w-10 h-px bg-[#1A382A]" />
            <span className="label-mono">NUTRA · BIOTECH · LIFE SCIENCES</span>
          </motion.div>

          <motion.h1
            data-testid={ATHENA.heroHeadline}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="font-display text-[14vw] sm:text-[10vw] lg:text-[7.2vw] xl:text-[110px] leading-[0.92] tracking-tight text-[#0A1713]"
          >
            The wisdom of <em className="italic text-[#1A382A]">nature</em>,
            <br />
            engineered with
            <br />
            the intelligence of <span className="text-[#B89B64]">science</span>.
          </motion.h1>

          <motion.p
            data-testid={ATHENA.heroSubheadline}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-[#3D4F46]"
          >
            Athena Nutrascience builds evidence-based nutraceuticals, advanced
            therapies, and translational research — partnering
            <br />
            with clinicians, formulators, and brands across three continents.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid={ATHENA.heroCtaPrimary}
              onClick={() => scrollTo("contact")}
              className="btn-primary"
            >
              Start a partnership <ArrowDownRight size={16} strokeWidth={1.5} />
            </button>
            <button
              data-testid={ATHENA.heroCtaSecondary}
              onClick={() => scrollTo("domains")}
              className="btn-secondary"
            >
              Explore capabilities
            </button>
          </motion.div>

          {/* Quick stat strip */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.7 }}
            className="mt-16 grid grid-cols-3 max-w-lg gap-6 border-t border-[#D5D1C8] pt-6"
          >
            {[
              ["20+", "Formulations"],
              ["8", "Active research programs"],
              ["GMP", "Certified facilities"],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="font-mono text-2xl text-[#1A382A]">{n}</div>
                <div className="text-xs text-[#3D4F46] leading-snug mt-1">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right: image with molecular overlay */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.2 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] w-full overflow-hidden border border-[#D5D1C8] bg-[#0A1713]">
            <img
              src={HERO_IMG}
              alt="Abstract molecular structure"
              className="absolute inset-0 w-full h-full object-cover opacity-90 mix-blend-luminosity"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#0A1713]/70 via-transparent to-[#0A1713]/40" />
            {/* badge */}
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-[#F8F7F4]">
              <div>
                <div className="label-mono text-[#B89B64]">/ DOSSIER 24-Δ</div>
                <div className="font-display text-2xl mt-1">Active Compound Mapping</div>
              </div>
              <Leaf size={28} className="text-[#B89B64]" strokeWidth={1.2} />
            </div>
          </div>
          {/* Floating caption card — aligned to left-center of the image */}
          <div className="group hidden lg:block absolute top-1/2 -translate-y-1/2 -left-6 bg-[#F8F7F4] border border-[#D5D1C8] p-6 w-64 shadow-sm cursor-pointer transition-all duration-500 ease-out hover:-left-2 hover:shadow-xl hover:border-[#1A382A]">
            <div className="label-mono transition-colors duration-500 group-hover:text-[#1A382A]">// ETHOS</div>
            <p className="mt-2 font-display text-xl leading-snug text-[#0A1713] transition-transform duration-500 group-hover:translate-x-1">
              Every molecule has a story. We listen first.
            </p>
            <div className="mt-4 h-px w-8 bg-[#B89B64] transition-all duration-500 group-hover:w-24" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
